import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'shared/components/Modal/Modal';
import { CtaButtons } from 'shared/components/Modal/types';
import BillingFormModalBody, {
  IFormInput,
  NEW_BILLING_FORM_MODAL_ID,
} from 'admin/modules/agreements/components/BillingFormModalBody/BillingFormModalBody';
import { useTheme } from 'styled-components';
import { AngleIcon } from 'shared/components/icons/AngleIcon';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { FlagKey } from 'api/types';
import { useBreakpoints } from 'hooks/useWindowSize';

export type IFormInputType = IFormInput;

type Props = {
  openBillingFormModal: boolean;
  handleCloseBillingForm: () => void;
  handleBackBillingForm: () => void;
  handleSubmitBillingForm: (formData: IFormInput) => void;
};

export function BillingAddressFormModal({
  openBillingFormModal,
  handleCloseBillingForm,
  handleBackBillingForm,
  handleSubmitBillingForm,
}: Props) {
  const {
    flags: { [FlagKey.onboardingRefresh]: onboardingRefresh },
  } = useFeatureFlags();

  const { t } = useTranslation();
  const theme = useTheme();
  const screenSize = useBreakpoints();

  const backButtonIcon = () => <AngleIcon color={theme.colors.buttonButtonPrimarySecondary2100} />;
  const body = (
    <BillingFormModalBody
      onSubmit={(formData) => {
        handleSubmitBillingForm(formData);
      }}
      placeholderEmpty
      marginTop
      borderRadius
    />
  );

  let backButtonMargin = '0px 196px 0px 0px';
  if (onboardingRefresh) {
    backButtonMargin = screenSize === 'sm' ? '0 24px 0 auto' : '0 auto 0 8px';
  }

  const ctaButtons: CtaButtons = {
    [onboardingRefresh ? 'tertiary' : 'secondary']: {
      label: t('cta.back'),
      onClick: handleBackBillingForm,
      Icon: onboardingRefresh ? undefined : backButtonIcon,
      margin: backButtonMargin,
      iconCenterAlign: true,
      iconBorderAlign: true,
    },
    primary: {
      formId: NEW_BILLING_FORM_MODAL_ID,
      type: 'submit',
      label: onboardingRefresh ? t('cta.update') : t('cta.next'),
      margin: onboardingRefresh ? '0' : '0px 10px 0px 0px',
    },
  };

  return (
    <Modal
      isOpen={openBillingFormModal}
      fitContent
      isFullHeight
      isFullWidth={screenSize === 'sm'}
      noCtaPadding
      styleVariant="tertiary"
      contentLabel="Modal"
      ctaButtons={ctaButtons}
      title={t('confirmBillingAddress.form')}
      onRequestClose={handleCloseBillingForm}
    >
      {body}
    </Modal>
  );
}

export default BillingAddressFormModal;
